import {z} from 'zod';

import {
  AbortSignalSchema,
  BaseVoidResponseSchema,
  CreatedAndUpdatedBySchema,
} from '@dms/api/shared';

import {UnionToSameValueObject} from 'shared/web';

/**
 * Common
 */
export const WarehouseArticlesRowSchema = z.object({
  id: z.string(),
  warehouseId: z.object({
    value: z.object({
      key: z.string(),
      label: z.string(),
    }),
  }),
});

export type WarehouseArticlesRow = z.infer<typeof WarehouseArticlesRowSchema>;

export const SalesPriceCalculationSchema = z.enum([
  'MARGIN_ON_BASE',
  'MARKUP_ON_BASE',
  'PROFIT_ON_BASE',
  'MANUAL',
  'RECOMMENDED_PRICE',
  'MARKETING_CODE',
]);

export type SalesPriceCalculation = z.infer<typeof SalesPriceCalculationSchema>;

export type SalesPriceCalculationObject = UnionToSameValueObject<SalesPriceCalculation>;

export const salesPriceCalculationObject: SalesPriceCalculationObject = {
  MARGIN_ON_BASE: 'MARGIN_ON_BASE',
  MARKUP_ON_BASE: 'MARKUP_ON_BASE',
  PROFIT_ON_BASE: 'PROFIT_ON_BASE',
  MANUAL: 'MANUAL',
  RECOMMENDED_PRICE: 'RECOMMENDED_PRICE',
  MARKETING_CODE: 'MARKETING_CODE',
};

export const BasePriceSourceSchema = z.enum([
  'AVG_PURCHASE',
  'LAST_PURCHASE',
  'RECOMMENDED',
  'MANUAL',
  'PURCHASE_PRICE_DEF_SUPPLIER',
]);

export type BasePriceSource = z.infer<typeof BasePriceSourceSchema>;
export type BasePriceSourceObject = UnionToSameValueObject<BasePriceSource>;
export const basePriceSourceObject: BasePriceSourceObject = {
  AVG_PURCHASE: 'AVG_PURCHASE',
  LAST_PURCHASE: 'LAST_PURCHASE',
  RECOMMENDED: 'RECOMMENDED',
  MANUAL: 'MANUAL',
  PURCHASE_PRICE_DEF_SUPPLIER: 'PURCHASE_PRICE_DEF_SUPPLIER',
};

export const RecommendedPriceSourceSchema = z.enum(['PRICE_LIST', 'DELIVERY_NOTE', 'MANUAL']);
export type RecommendedPriceSource = z.infer<typeof RecommendedPriceSourceSchema>;

export const PurchasePriceDefSupplierSourceSchema = z.enum([
  'PRICE_LIST',
  'DELIVERY_NOTE',
  'MANUAL',
]);
export type PurchasePriceDefSupplierSource = z.infer<typeof PurchasePriceDefSupplierSourceSchema>;

export const DispensingPricesSchema = z.object({
  basePriceWithoutVat: z.number().nullish(),
  basePriceWithVat: z.number().nullish(),
  saleBasePriceWithoutVat: z.number().nullish(),
  saleBasePriceWithVat: z.number().nullish(),
  saleBaseProfit: z.number().nullish(),
  saleBaseMarginPercent: z.number().nullish(),
  saleBaseMarkUpPercent: z.number().nullish(),
  warrantyPriceWithoutVat: z.number().nullish(),
  warrantyPriceWithVat: z.number().nullish(),
  warrantyProfit: z.number().nullish(),
  warrantyMarginPercent: z.number().nullish(),
  warrantyMarkUpPercent: z.number().nullish(),
});
export type DispensingPrices = z.infer<typeof DispensingPricesSchema>;

export const PricesSettingsSchema = z.object({
  avgPurchasePriceWithoutVat: z.number().nullish(),
  avgPurchasePriceWithVat: z.number().nullish(),
  lastPurchasePriceWithoutVat: z.number().nullish(),
  lastPurchasePriceWithVat: z.number().nullish(),
  recommendedPurchasePriceWithoutVat: z.number().nullish(),
  recommendedPurchasePriceWithVat: z.number().nullish(),
  purchasePriceDefSupplierWithoutVat: z.number().nullish(),
  purchasePriceDefSupplierWithVat: z.number().nullish(),
  salesPriceCalculation: SalesPriceCalculationSchema,
  basePriceSource: BasePriceSourceSchema,
  unitSalesPriceWithVat: z.boolean(),
  recommendedPriceSource: RecommendedPriceSourceSchema.nullish(),
  purchasePriceDefSupplierSource: PurchasePriceDefSupplierSourceSchema.nullish(),
});
export type PricesSettings = z.infer<typeof PricesSettingsSchema>;

export const ArticleTreeFolderSchema = z.object({
  leafId: z.string().nullable(),
});
export type ArticleTreeFolder = z.infer<typeof ArticleTreeFolderSchema>;

export const StockManagementTypeSchema = z.enum(['MANUAL', 'MIN_MAX']);
export type StockManagementType = z.infer<typeof StockManagementTypeSchema>;

export const BaseArticleSchema = z.object({
  id: z.string(),
  warehouseId: z.string(),
  supplierId: z.string().optional(),
  manufacturerId: z.string(),
  makeCode: z.string().optional(),
  name: z.string(),
  description: z.string().optional(),
  stockNumber: z.string().optional(),
  manufacturerNumber: z.string(),
  manufacturerNumberNormalized: z.string().optional(),
  manufacturerName: z.string().optional(),
  handlingUnit: z.string(),
  dispensingUnit: z.number().nullish(),
  receivedHandlingQuantity: z.number().optional(),
  vatType: z.string().optional(),
  storageLocation: z.string().optional(),
  storageLocationId: z.string().nullish(),
  supplierDiscountGroupId: z.string().optional(),
  treeFolder: ArticleTreeFolderSchema,
  marketingCode: z.string().optional(),
  pricesSettings: PricesSettingsSchema.partial(),
  dispensingPrices: DispensingPricesSchema.partial(),
  isTemporary: z.boolean().optional(), // exception where bool could be undefined,
  isRemovable: z.boolean(),
  workItemGroupId: z.string().optional(),
  isOneTimeSale: z.boolean(),
  stockManagement: z.object({
    stockManagementType: StockManagementTypeSchema,
    stockManagementMinQuantity: z.number().optional(),
    stockManagementMaxQuantity: z.number().optional(),
    currentStockQuantity: z.number().optional(),
    reservationQuantity: z.number().optional(),
    returnToSupplierQuantity: z.number().optional(),
    returnToWarehouseQuantity: z.number().optional(),
    orderRequestsQuantity: z.number().optional(),
    orderPendingQuantity: z.number().optional(),
    orderedQuantity: z.number().optional(),
    receiveQuantity: z.number().optional(),
    forecastQuantity: z.number().optional(),
    toOrderQuantity: z.number().optional(),
  }),
});
export type BaseArticle = z.infer<typeof BaseArticleSchema>;

/**
 * GET
 */
export const GetArticleRequestSchema = z.object({
  warehouseId: z.string(),
  articleId: z.string(),
  // Opts this call into the route version that checks the warehouse ACL scopes
  isAclScoped: z.boolean().optional(),
});
export type GetArticleRequest = z.infer<typeof GetArticleRequestSchema>;

export const GetArticleResponseSchema = BaseArticleSchema.merge(CreatedAndUpdatedBySchema).extend({
  // Optional: the measurement unit stays editable when the backend omits the flag
  isHandlingUnitEditable: z.boolean().optional(),
  // Optional: a missing object or flag means the action is allowed
  allowedPermissions: z
    .object({
      read: z.boolean().optional(),
      manualReservation: z.boolean().optional(),
      edit: z.boolean().optional(),
      delete: z.boolean().optional(),
      rename: z.boolean().optional(),
      changePrices: z.boolean().optional(),
    })
    .optional(),
});
export type GetArticleResponse = z.infer<typeof GetArticleResponseSchema>;

export const GetArticleIdByManufacturerNumberAndManufacturerIdRequestSchema = z.object({
  warehouseId: z.string(),
  manufacturerNumber: z.string(),
  manufacturerId: z.string(),
  supplierId: z.string().optional(),
  supplierOrderTypeCode: z.string().max(100).optional(),
});
export type GetArticleIdByManufacturerNumberAndManufacturerIdRequest = z.infer<
  typeof GetArticleIdByManufacturerNumberAndManufacturerIdRequestSchema
>;

export const GetArticleIdByManufacturerNumberAndManufacturerIdResponseSchema = z
  .object({
    articleId: z.string().nullable(),
    name: z.string().nullable(),
    measurementUnitId: z.string().nullable(),
  })
  .nullable();
export type GetArticleIdByManufacturerNumberAndManufacturerIdResponse = z.infer<
  typeof GetArticleIdByManufacturerNumberAndManufacturerIdResponseSchema
>;

export const GetSupplierArticleRequestSchema = z.object({
  supplierArticleId: z.string(),
});
export type GetSupplierArticleRequest = z.infer<typeof GetSupplierArticleRequestSchema>;

export const GetSupplierArticleResponseSchema = z.object({
  supplierArticleId: z.string(),
  supplierId: z.string(),
  supplierName: z.string().nullish(),
  supplierOrderingNumber: z.string().nullish(),
  articleId: z.string().nullish(),
  articleName: z.string().nullish(),
  manufacturerNumber: z.string().nullish(),
  manufacturerName: z.string().nullish(),
  supplierQuantity: z.number(),
  supplierUnitId: z.string(),
  warehouseQuantity: z.number(),
  warehouseUnitId: z.string(),
  recommendedPriceWithoutVat: z.number().nullish(),
  purchasePriceWithoutVat: z.number().nullish(),
  supplierBulkPackageQuantity: z.number().nullish(),
  currency: z.string().nullish(),
  supplierDiscountGroupCode: z.string().nullish(), // original discount group code taken from supplier price list
  supplierArticleSupplierDiscountGroupCode: z.string().nullish(), // editable discount group code from settings of supplier discount group
  recommendedPriceSource: RecommendedPriceSourceSchema.nullish(),
  purchasePriceSource: PurchasePriceDefSupplierSourceSchema.nullish(),
  supplierManufacturerCode: z.string().nullish(),
});
export type GetSupplierArticleResponse = z.infer<typeof GetSupplierArticleResponseSchema>;

/**
 * POST
 */
export const PostArticleRequestSchema = z.object({
  warehouseId: z.string(),
  // Opts this call into the route version that checks the warehouse ACL scopes
  isAclScoped: z.boolean().optional(),
  body: z.object({
    supplierId: z.string().nullish(),
    supplierOrderTypeCode: z.string().max(100).optional(),
    manufacturerId: z.string(),
    makeCode: z.string().optional(),
    name: z.string(),
    description: z.string().optional(),
    stockNumber: z.string().optional(),
    manufacturerNumber: z.string(),
    handlingUnit: z.string(),
    dispensingUnit: z.number().nullish(),
    vatType: z.string().optional(),
    storageLocation: z.string().nullish(),
    storageLocationId: z.string().nullish(),
    discountGroup: z.string().optional(),
    treeFolder: ArticleTreeFolderSchema,
    marketingCode: z.string().optional(),
    pricesSettings: PricesSettingsSchema.optional(),
    dispensingPrices: DispensingPricesSchema.optional(),
  }),
});
export type PostArticleRequest = z.infer<typeof PostArticleRequestSchema>;

export const PostArticleResponseSchema = z.object({
  articleId: z.string(),
});
export type PostArticleResponse = z.infer<typeof PostArticleResponseSchema>;

export const PostArticleCreationStressTestRequestSchema = z.object({
  warehouseId: z.string(),
  supplierId: z.string(),
  volume: z.number().int(),
  namePattern: z.string().nullish(),
  oemPattern: z.string().nullish(),
  testOnly: z.boolean(),
});
export type PostArticleCreationStressTestRequest = z.infer<
  typeof PostArticleCreationStressTestRequestSchema
>;

export const PostArticleCreationStressTestResponseSchema = BaseVoidResponseSchema;
export type PostArticleCreationStressTestResponse = z.infer<
  typeof PostArticleCreationStressTestResponseSchema
>;

/**
 * PATCH
 */
export const PatchArticleRequestSchema = z.object({
  warehouseId: z.string(),
  articleId: z.string(),
  // Opts this call into the route version that checks the warehouse ACL scopes
  isAclScoped: z.boolean().optional(),
  body: z.object({
    supplierId: z.string(),
    manufacturerId: z.string(),
    name: z.string(),
    description: z.string().nullish(),
    manufacturerNumber: z.string(),
    handlingUnit: z.string(),
    dispensingUnit: z.number().nullish(),
    vatType: z.string().nullish(),
    storageLocation: z.string().nullish(),
    storageLocationId: z.string().nullish(),
    treeFolder: ArticleTreeFolderSchema,
    marketingCode: z.string().nullish(),
    pricesSettings: PricesSettingsSchema.partial(),
    dispensingPrices: DispensingPricesSchema.partial(),
    workItemGroupId: z.string().nullish(),
    isOneTimeSale: z.boolean(),
    stockManagement: z.object({
      stockManagementType: z.enum(['MANUAL', 'MIN_MAX']),
      stockManagementMinQuantity: z.number().nullish(),
      stockManagementMaxQuantity: z.number().nullish(),
    }),
  }),
});
export type PatchArticleRequest = z.infer<typeof PatchArticleRequestSchema>;

export const PatchArticleResponseSchema = BaseVoidResponseSchema;
export type PatchArticleResponse = z.infer<typeof PatchArticleResponseSchema>;

export const PatchArticleDefaultSupplierRequestSchema = z.object({
  articleId: z.string(),
  body: z.object({
    supplierId: z.string(),
  }),
});
export type PatchArticleDefaultSupplierRequest = z.infer<
  typeof PatchArticleDefaultSupplierRequestSchema
>;

export const PatchArticleDefatilSupplierResponseSchema = BaseVoidResponseSchema;
export type PatchArticleDefaultSupplierResponse = z.infer<
  typeof PatchArticleDefatilSupplierResponseSchema
>;

export const PatchArticleInfoRequestSchema = z.object({
  articleId: z.string(),
  body: z.object({
    name: z.string(),
  }),
});
export type PatchArticleInfoRequest = z.infer<typeof PatchArticleInfoRequestSchema>;

export const PatchArticleInfoResponseSchema = BaseVoidResponseSchema;
export type PatchArticleInfoResponse = z.infer<typeof PatchArticleInfoResponseSchema>;

export const PatchSupplierArticleRequestSchema = z.object({
  supplierArticleId: z.string(),
  body: z.object({
    articleId: z.string(),
    manufacturerNumber: z.string(),
    supplierArticle: z.object({
      supplierQuantity: z.number(),
      supplierUnitId: z.string(),
      warehouseQuantity: z.number(),
      supplierOrderingNumber: z.string().nullish(),
      supplierBulkPackageQuantity: z.number().nullish(),
      supplierId: z.string(),
      supplierDiscountGroupCode: z.string().nullish(),
    }),
  }),
});
export type PatchSupplierArticleRequest = z.infer<typeof PatchSupplierArticleRequestSchema>;

export const PatchSupplierArticleResponseSchema = BaseVoidResponseSchema;
export type PatchSupplierArticleResponse = z.infer<typeof PatchSupplierArticleResponseSchema>;

/**
 * DELETE
 */
export const DeleteArticleRequestSchema = z.object({
  articleId: z.array(z.string()),
});
export type DeleteArticleRequest = z.infer<typeof DeleteArticleRequestSchema>;

export const DeleteArticleResponseSchema = BaseVoidResponseSchema;
export type DeleteArticleResponse = z.infer<typeof DeleteArticleResponseSchema>;

/**
 * PUT
 */
export const BaseRecalculationSchema = z.object({
  supplierId: z.string().nullish(),
  manufacturerId: z.string().nullish(),
  manufacturerNumber: z.string().nullish(),
  vatType: z.string().nullish(),
  discountGroup: z.string().nullish(),
  marketingCode: z.string().nullish(),
  pricesSettings: PricesSettingsSchema.partial(),
  dispensingPrices: DispensingPricesSchema.partial(),
});
export type BaseRecalculation = z.infer<typeof BaseRecalculationSchema>;

const ChangedFieldValueSchema = z.object({
  field: z.enum([
    'vatType',
    'supplierId',
    'marketingCode',
    'recommendedPriceWithOutVat',
    'recommendedPriceWithVat',
    'recommendedPurchasePriceWithOutVat',
    'recommendedPurchasePriceWithVat',
    'purchasePriceDefSupplierWithoutVat',
    'saleBasePriceWithoutVat',
    'saleBasePriceWithVat',
    'saleBaseMarkUpPercent',
    'saleBaseProfit',
    'saleBaseMarginPercent',
    'warrantyPriceWithoutVat',
    'warrantyPriceWithVat',
    'warrantyMarkUpPercent',
    'warrantyProfit',
    'warrantyMarginPercent',
    'purchasePrice',
  ]),
  value: z.string(),
});
export type ChangedFieldValue = z.infer<typeof ChangedFieldValueSchema>;

export const PutArticleCalculationRequestSchema = z.object({
  warehouseId: z.string(),
  articleId: z.string(),
  body: z.object({
    supplierOrderTypeCode: z.string().max(100).optional(),
    changedFieldValue: ChangedFieldValueSchema,
    currentFeFieldsValues: BaseRecalculationSchema,
  }),
  signal: AbortSignalSchema,
});
export type PutArticleCalculationRequest = z.infer<typeof PutArticleCalculationRequestSchema>;

export const PutArticleCalculationResponseSchema = z.object({
  currency: z.string(),
  pricesSettings: PricesSettingsSchema,
  dispensingPrices: DispensingPricesSchema,
});
export type PutArticleCalculationResponse = z.infer<typeof PutArticleCalculationResponseSchema>;

export const PutSupplierArticlePromptValidationRequestSchema = z.object({
  supplierId: z.string(),
  articleId: z.string(),
  manufacturerNumber: z.string(),
  supplierOrderingNumber: z.string().nullish(),
  supplierToWarehouseMapping: z.object({
    supplierQuantity: z.number(),
    supplierUnitId: z.string(),
    supplierBulkPackageQuantity: z.number().nullish(),
    warehouseQuantity: z.number(),
  }),
});
export type PutSupplierArticlePromptValidationRequest = z.infer<
  typeof PutSupplierArticlePromptValidationRequestSchema
>;

export const PutSupplierArticlePromptValidationResponseSchema = z.object({
  isChanged: z.boolean(),
});
export type PutSupplierArticlePromptValidationResponse = z.infer<
  typeof PutSupplierArticlePromptValidationResponseSchema
>;

export const PostArticleAssignWorkItemGroupRequestSchema = z.object({
  articleIds: z.array(z.string()),
  workItemGroupId: z.string(),
});
export type PostArticleAssignWorkItemGroupRequest = z.infer<
  typeof PostArticleAssignWorkItemGroupRequestSchema
>;

export const PostArticleAssignWorkItemGroupResponseSchema = BaseVoidResponseSchema;
export type PostArticleAssignWorkItemGroupResponse = z.infer<
  typeof PostArticleAssignWorkItemGroupResponseSchema
>;

export const PutArticleStockManagementCalculationRequestSchema = z.object({
  warehouseId: z.string(),
  articleId: z.string(),
  body: z.object({
    minQuantity: z.number(),
    maxQuantity: z.number(),
    bulkPackage: z.number().optional(),
  }),
});
export type PutArticleStockManagementCalculationRequest = z.infer<
  typeof PutArticleStockManagementCalculationRequestSchema
>;

export const PutArticleStockManagementCalculationResponseSchema = z.object({
  articleId: z.string(),
  minQuantity: z.number(),
  maxQuantity: z.number(),
  bulkPackage: z.number(),
  currentStockQuantity: z.number(),
  reservationQuantity: z.number(),
  orderRequestsQuantity: z.number(),
  returnToSupplierQuantity: z.number(),
  returnToWarehouseQuantity: z.number(),
  orderPendingQuantity: z.number(),
  orderedQuantity: z.number(),
  receiveQuantity: z.number(),
  forecastQuantity: z.number(),
  toOrderQuantity: z.number(),
});
export type PutArticleStockManagementCalculationResponse = z.infer<
  typeof PutArticleStockManagementCalculationResponseSchema
>;
